import { transition } from '@hover/blueprint';
import type { ChakraProps, SystemStyleObject } from '@hover/blueprint/chakra';
import { resetFocus } from '@hover/blueprint/foundation';
import { position } from 'polished';
import React from 'react';

import type { ButtonProps } from 'src/shared/components/Button';
import { Button } from 'src/shared/components/Button';
import { TranslationNamespace, useTranslate } from 'src/shared/hooks';

export interface SelectionProps {
  isSelected?: boolean;
  offset?: string;
  isThin?: boolean;
  sx?: SystemStyleObject;
}

export interface SelectionButtonProps extends ButtonProps, SelectionProps {}

const INDICATOR_OFFSET = '0px';

export const selectionStyles = ({
  isSelected,
  offset = INDICATOR_OFFSET,
  isThin = false,
  sx,
}: SelectionProps) => {
  const shadow = isThin ? 'ringPrimary' : 'haloPrimary';

  return {
    _after: {
      borderColor: 'primary.base',
      borderWidth: isThin ? '2px' : '3px',
      borderStyle: 'solid',
      borderRadius: 'inherit',
      content: '""',
      opacity: isSelected ? 1 : 0,
      shadow,
      ...transition.easing250('opacity'),
      ...position('absolute', offset),
    },
    _hover: {
      shadow: isSelected ? 'invisibleNeutral' : shadow,
    },
    ...transition.easing250('box-shadow'),
    background: isSelected ? 'transparent' : 'neutral.200',
    borderWidth: '0',
    overflow: 'visible',
    padding: '0px',
    sx: { ...sx, ...resetFocus({ shadow }) } as SystemStyleObject,
  } satisfies ChakraProps;
};

export const SelectionButton = ({
  isSelected,
  offset,
  ...props
}: SelectionButtonProps) => {
  const t = useTranslate(TranslationNamespace.DESIGN_STUDIO);

  return (
    <Button
      aria-pressed={isSelected}
      label={t('components.selectionButton')}
      {...props}
      {...selectionStyles({ isSelected, offset })}
      shape="circle"
    />
  );
};
